import { apiClient } from '@services/api';
import CircularProgress from '@material-ui/core/CircularProgress';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Feature CSAT (2026-08-28) — página pública de avaliação pós-serviço, sem
// login. Acessada via link no e-mail enviado pelo comando
// app:send-satisfaction-surveys, 3 dias após o último serviço do cliente.
// Paleta espelha a mesma da presale pública (voucherLink/presale.jsx) pra
// manter consistência visual entre as páginas públicas do sistema.

const C = {
  pageBg: '#0a1628',
  card: '#132035',
  cardInner: '#0f1c2e',
  accent: '#62DEC2',
  accentGold: '#F5C842',
  label: '#6e8eb0',
  value: '#dce9fb',
  muted: '#4a6380',
  sep: '#1a3050',
};

const STAR_COUNT = 5;

const Star = ({ filled, onClick, onMouseEnter, onMouseLeave, size = 44 }) => (
  <span
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      cursor: 'pointer',
      fontSize: size,
      color: filled ? C.accentGold : C.muted,
      transition: 'color 0.15s ease, transform 0.1s ease',
      lineHeight: 1,
      userSelect: 'none',
    }}
  >
    ★
  </span>
);

const EvaluationPage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [alreadyAnswered, setAlreadyAnswered] = useState(false);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    apiClient.httpInstance
      .get(`/public/evaluations/${token}`)
      .then((r) => {
        setCustomerName(r.data?.customer_name || '');
        setAlreadyAnswered(!!r.data?.answered);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = () => {
    if (!rating) {
      setErrorMsg('Escolha de 1 a 5 estrelas antes de enviar.');
      return;
    }
    setErrorMsg('');
    setSubmitting(true);
    apiClient.httpInstance
      .post(`/public/evaluations/${token}`, { rating, comment })
      .then(() => setSubmitted(true))
      .catch((err) => {
        setErrorMsg(err?.response?.data?.message || 'Não foi possível enviar sua avaliação. Tente novamente.');
      })
      .finally(() => setSubmitting(false));
  };

  const wrapperStyle = {
    minHeight: '100vh',
    background: C.pageBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: "'Poppins', Arial, sans-serif",
  };

  const cardStyle = {
    background: C.card,
    borderRadius: 16,
    padding: '40px 32px',
    maxWidth: 480,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
  };

  if (loading) {
    return (
      <div style={wrapperStyle}>
        <CircularProgress style={{ color: C.accent }} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <p style={{ color: C.value, fontSize: 15 }}>
            Esse link de avaliação não foi encontrado ou expirou.
          </p>
        </div>
      </div>
    );
  }

  if (submitted || alreadyAnswered) {
    return (
      <div style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💙</div>
          <p style={{ color: C.value, fontSize: 17, fontWeight: 600, marginBottom: 8 }}>
            Obrigado pela sua avaliação!
          </p>
          <p style={{ color: C.label, fontSize: 14 }}>
            Sua opinião é muito importante para a Full Tour.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div style={cardStyle}>
        <p style={{ color: C.accent, fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
          Full Tour
        </p>
        <p style={{ color: C.value, fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
          Olá{customerName ? `, ${customerName}` : ''}!
        </p>
        <p style={{ color: C.label, fontSize: 14, marginBottom: 28, lineHeight: 1.5 }}>
          Como foi sua experiência com a Full Tour?
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
          {Array.from({ length: STAR_COUNT }, (_, i) => i + 1).map((n) => (
            <Star
              key={n}
              filled={n <= (hoverRating || rating)}
              onClick={() => setRating(n)}
              onMouseEnter={() => setHoverRating(n)}
              onMouseLeave={() => setHoverRating(0)}
            />
          ))}
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Quer deixar um comentário? (opcional)"
          rows={4}
          style={{
            width: '100%',
            background: C.cardInner,
            border: `1px solid ${C.sep}`,
            borderRadius: 10,
            color: C.value,
            fontSize: 14,
            padding: 14,
            marginBottom: 16,
            resize: 'vertical',
            fontFamily: "'Poppins', Arial, sans-serif",
            boxSizing: 'border-box',
          }}
        />

        {errorMsg && (
          <p style={{ color: '#e05252', fontSize: 13, marginBottom: 12 }}>{errorMsg}</p>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            width: '100%',
            background: submitting ? C.muted : C.accent,
            color: '#0a1628',
            border: 'none',
            borderRadius: 10,
            padding: '14px 0',
            fontSize: 15,
            fontWeight: 700,
            cursor: submitting ? 'default' : 'pointer',
            fontFamily: "'Poppins', Arial, sans-serif",
          }}
        >
          {submitting ? 'Enviando...' : 'Enviar avaliação'}
        </button>
      </div>
    </div>
  );
};

export default EvaluationPage;
